import ApiService from './apiService';

/**
 * Service pour gérer les opérations liées aux ventes
 */
class SalesService {
    /**
    /**
     * Récupérer la liste des ventes avec filtres et pagination
     *
     * @param {Object} params Paramètres de filtrage et pagination :
     *   @param {string}   [params.search]            Recherche texte (max: 100 caractères)
     *   @param {number}   [params.per_page]          Nombre de résultats par page (min:1, max:100)
     *   @param {number}   [params.page]              Numéro de page (min:1)
     *   @param {Array}    [params.with]              Relations à charger (array, ex: ['user','saleItems','saleItems.product','payment'])
     *   @param {number}   [params.cash_register_id]  Filtrer par caisse (ID existant)
     *   @param {string}   [params.date_from]         Date de début (YYYY-MM-DD)
     *   @param {string}   [params.date_to]           Date de fin (YYYY-MM-DD, >= date_from)
     *   @param {string}   [params.type]              Type de vente ('single','deferred')
     *   @param {string}   [params.status]            Statut de la vente ('completed','refunded','partial_refund')
     * @returns {Promise} Réponse de l'API avec la liste des ventes
     */
    getSales(params = {}) {
        return ApiService.get('/sales', params);
    }

    /**
     * Récupérer une vente par son ID
     * @param {Number} id ID de la vente
     * @param {Object} params Paramètres additionnels (relations à charger)
     * @returns {Promise} Réponse de l'API avec les détails de la vente
     */
    getSale(id, params = {}) {
        return ApiService.get(`/sales/${id}`, params);
    }

    /**
     * Créer une nouvelle vente
     * @param {Object} saleData Données de la vente à créer
     * @returns {Promise} Réponse de l'API avec la vente créée
     */
    createSale(saleData) {
        return ApiService.post('/sales', saleData);
    }

    /**
     * Effectuer un remboursement complet ou partiel d'une vente
     * @param {Number} id ID de la vente
     * @param {Object} refundData Données du remboursement
     * @returns {Promise} Réponse de l'API avec la vente mise à jour
     */
    refundSale(id, refundData) {
        return ApiService.post(`/sales/${id}/refund`, refundData);
    }

    /**
     * Obtenir un résumé des ventes (statistiques)
     * @param {Object} params Paramètres de filtrage (dates, groupement)
     * @returns {Promise} Réponse de l'API avec les statistiques
     */
    getSalesSummary(params = {}) {
        return ApiService.get('/sales/summary', params);
    }

    /**
     * Rechercher des ventes
     * @param {String} query Terme de recherche
     * @param {Object} options Options additionnelles (dates, statut, etc.)
     * @returns {Promise} Réponse de l'API avec les résultats de recherche
     */
    searchSales(query, options = {}) {
        return ApiService.get('/sales', {
            search: query,
            ...options
        });
    }

    /**
     * Obtenir les ventes par période
     * @param {String} dateFrom Date de début (format YYYY-MM-DD)
     * @param {String} dateTo Date de fin (format YYYY-MM-DD)
     * @param {Object} params Paramètres additionnels
     * @returns {Promise} Réponse de l'API avec les ventes de la période
     */
    getSalesByPeriod(dateFrom, dateTo, params = {}) {
        return ApiService.get('/sales', {
            date_from: dateFrom,
            date_to: dateTo,
            ...params
        });
    }

    /**
     * Obtenir les ventes par statut
     * @param {String} status Statut des ventes (completed, refunded, partial_refund)
     * @param {Object} params Paramètres additionnels
     * @returns {Promise} Réponse de l'API avec les ventes filtrées par statut
     */
    getSalesByStatus(status, params = {}) {
        return ApiService.get('/sales', {
            status: status,
            ...params
        });
    }

    /**
     * Obtenir des statistiques des ventes regroupées
     * @param {String} groupBy Type de regroupement (day, week, month, year, product, payment_type)
     * @param {String} dateFrom Date de début (format YYYY-MM-DD)
     * @param {String} dateTo Date de fin (format YYYY-MM-DD)
     * @returns {Promise} Réponse de l'API avec les statistiques regroupées
     */
    getSalesStatsGrouped(groupBy, dateFrom, dateTo) {
        return ApiService.get('/sales/summary', {
            group_by: groupBy,
            date_from: dateFrom,
            date_to: dateTo
        });
    }

    /**
     * Préparer les données d'une nouvelle vente
     * @param {Array} items Articles à vendre avec quantité
     * @param {Object} payment Paiements de la vente
     * @param {Number} discount Remise éventuelle
     * @returns {Object} Données formatées pour l'API
     */
    prepareSaleData(items, payment, discount = 0) {
        return {
            items: items.map(item => ({
                product_id: item.product_id,
                quantity: item.quantity,
                price_excl_tax: item.price_excl_tax
            })),
            payment: {
                payment_type: payment.payment_type,
                amount: payment.amount
            },
            discount: discount || 0
        };
    }
    formatSaleForDisplay(sale) {
        if (!sale) return null;

        return {
            ...sale,
            formattedDate: this.formatDate(sale.sale_date),
            formattedTotal: this.formatCurrency(sale.total_incl_tax),
            formattedStatus: this.getStatusLabel(sale.status),
            paymentMethods: this.extractPaymentMethods(sale.payments)
        };
    }

    /**
     * Formatter une date
     * @param {String} dateString Date au format ISO
     * @returns {String} Date formatée
     */
    formatDate(dateString) {
        if (!dateString) return '';

        const date = new Date(dateString);
        return date.toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    }

    /**
     * Formatter un montant en devise
     * @param {Number} amount Montant
     * @returns {String} Montant formaté
     */
    formatCurrency(amount) {
        if (amount === undefined || amount === null) return '';

        return new Intl.NumberFormat('fr-FR', {
            style: 'currency',
            currency: 'XOF',
            minimumFractionDigits: 0
        }).format(amount);
    }

    /**
     * Obtenir le libellé d'un statut
     * @param {String} status Code du statut
     * @returns {String} Libellé du statut
     */
    getStatusLabel(status) {
        const statusLabels = {
            'completed': 'Complétée',
            'refunded': 'Remboursée',
            'partial_refund': 'Remboursement partiel'
        };

        return statusLabels[status] || status;
    }

    /**
     * Extraire les méthodes de paiement d'une vente
     * @param {Array} payments Liste des paiements
     * @returns {String} Méthodes de paiement formatées
     */
    extractPaymentMethods(payments) {
        if (!payments || !payments.length) return '';

        const paymentTypes = {
            'cash': 'Espèces',
            'card': 'Carte',
            'mobile_money': 'Mobile Money',
            'check': 'Chèque',
            'other': 'Autre',
            'refund': 'Remboursement'
        };

        const methods = payments
            .filter(p => p.amount > 0)
            .map(p => paymentTypes[p.payment_type] || p.payment_type);

        return [...new Set(methods)].join(', ');
    }

    /**
     * Télécharger la facture PDF d'une vente
     * @param {Number|String} saleId ID de la vente
     * @returns {Promise<Blob>} Fichier PDF en blob
     */
    async downloadInvoice(saleId, open = false, clientInfo = {}) {
        if (!saleId) throw new Error('ID de vente requis');

        const response = await ApiService.post(`/sales/${saleId}/invoice`, {clientName: clientInfo.name, clientPhone: clientInfo.phone}, { responseType: 'blob' });
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);

        if (open) {
            // Ouvrir dans un nouvel onglet pour prévisualisation
            window.open(url, '_blank');
        } else {
            // Télécharger directement
            const link = document.createElement('a');
            link.href = url;
            link.download = `recu-${saleId}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }

        // Nettoyer l'URL après un délai pour permettre l'ouverture
        setTimeout(() => window.URL.revokeObjectURL(url), 100);
    }
}

export default new SalesService();